import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import BehaviorTimeline from './BehaviorTimeline';

const REFRESH_INTERVAL = 30;

interface SessionSummary {
  session_id: string;
  first_seen: string;
  last_seen: string;
  event_count: number;
  device: string | null;
  referrer: string | null;
  converted: boolean;
  opened_form: boolean;
  reached_step2: boolean;
  clicked_button: boolean;
  max_scroll: number;
  time_on_page: number | null;
  lead_name: string | null;
}

function getSourceLabel(referrer: string | null): { label: string; icon: string; color: string } {
  if (!referrer) return { label: 'Direkt', icon: 'ri-compass-3-line', color: '#a78bfa' };
  if (referrer.includes('google')) return { label: 'Google', icon: 'ri-google-line', color: '#4ade80' };
  if (referrer.includes('instagram')) return { label: 'Instagram', icon: 'ri-instagram-line', color: '#f472b6' };
  if (referrer.includes('linkedin')) return { label: 'LinkedIn', icon: 'ri-linkedin-box-line', color: '#60a5fa' };
  if (referrer.includes('twitter') || referrer.includes('x.com')) return { label: 'Twitter/X', icon: 'ri-twitter-x-line', color: '#e2e8f0' };
  if (referrer.includes('facebook')) return { label: 'Facebook', icon: 'ri-facebook-circle-line', color: '#60a5fa' };
  if (referrer.includes('readdy')) return { label: 'Readdy', icon: 'ri-code-box-line', color: '#f0b429' };
  return { label: new URL(referrer).hostname.replace('www.', ''), icon: 'ri-global-line', color: '#94a3b8' };
}

function getJourneyStage(s: SessionSummary): { label: string; color: string; bg: string; icon: string } {
  if (s.converted) return { label: 'Lead Oldu', color: '#4ade80', bg: 'rgba(74,222,128,0.12)', icon: 'ri-checkbox-circle-line' };
  if (s.reached_step2) return { label: 'Form Adım 2\'de Bıraktı', color: '#f0b429', bg: 'rgba(240,180,41,0.12)', icon: 'ri-error-warning-line' };
  if (s.opened_form) return { label: 'Formu Açtı, Bıraktı', color: '#fb923c', bg: 'rgba(251,146,60,0.12)', icon: 'ri-door-open-line' };
  if (s.clicked_button) return { label: 'Butona Bastı', color: '#00d4ff', bg: 'rgba(0,212,255,0.10)', icon: 'ri-cursor-line' };
  if (s.max_scroll >= 50) return { label: 'Derinlemesine Gezdi', color: '#a78bfa', bg: 'rgba(167,139,250,0.10)', icon: 'ri-arrow-down-double-line' };
  if (s.max_scroll >= 25) return { label: 'Kısmen Gezdi', color: '#94a3b8', bg: 'rgba(148,163,184,0.10)', icon: 'ri-arrow-down-line' };
  return { label: 'Hemen Ayrıldı', color: '#f87171', bg: 'rgba(248,113,113,0.10)', icon: 'ri-logout-circle-line' };
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) return `Bugün ${formatTime(iso)}`;
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function formatDuration(secs: number | null): string {
  if (!secs) return '—';
  if (secs < 60) return `${secs}sn`;
  return `${Math.floor(secs / 60)}dk ${secs % 60}sn`;
}

function isActive(lastSeen: string): boolean {
  return Date.now() - new Date(lastSeen).getTime() < 5 * 60 * 1000;
}

interface Props {
  leadSessionMap: Record<string, string>;
}

export default function VisitorSessionList({ leadSessionMap }: Props) {
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const [filter, setFilter] = useState<'all' | 'converted' | 'dropped' | 'active'>('all');
  const [timelineSession, setTimelineSession] = useState<{ sessionId: string; name: string } | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchSessions = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);

    const { data } = await supabase
      .from('page_events')
      .select('session_id, event_type, event_label, extra, device, referrer, created_at')
      .order('created_at', { ascending: false });

    if (!data) { setLoading(false); setRefreshing(false); return; }

    // Group by session
    const map = new Map<string, SessionSummary>();
    (data as Array<{
      session_id: string;
      event_type: string;
      event_label: string | null;
      extra: Record<string, unknown> | null;
      device: string | null;
      referrer: string | null;
      created_at: string;
    }>).forEach((ev) => {
      const sid = ev.session_id;
      if (!map.has(sid)) {
        map.set(sid, {
          session_id: sid,
          first_seen: ev.created_at,
          last_seen: ev.created_at,
          event_count: 0,
          device: ev.device,
          referrer: ev.referrer,
          converted: false,
          opened_form: false,
          reached_step2: false,
          clicked_button: false,
          max_scroll: 0,
          time_on_page: null,
          lead_name: leadSessionMap[sid] ?? null,
        });
      }
      const s = map.get(sid)!;
      s.event_count += 1;
      if (ev.created_at < s.first_seen) s.first_seen = ev.created_at;
      if (ev.created_at > s.last_seen) s.last_seen = ev.created_at;
      if (ev.event_type === 'whatsapp_submit') s.converted = true;
      if (ev.event_type === 'modal_open') s.opened_form = true;
      if (ev.event_type === 'modal_step2') s.reached_step2 = true;
      if (ev.event_type === 'button_click') s.clicked_button = true;
      if (ev.event_type === 'scroll_depth') {
        const d = (ev.extra?.depth_percent as number) ?? 0;
        if (d > s.max_scroll) s.max_scroll = d;
      }
      if (ev.event_type === 'page_leave') {
        s.time_on_page = (ev.extra?.seconds_on_page as number) ?? null;
      }
    });

    const sorted = Array.from(map.values()).sort(
      (a, b) => new Date(b.first_seen).getTime() - new Date(a.first_seen).getTime()
    );
    setSessions(sorted);
    setLoading(false);
    setRefreshing(false);
    setLastUpdated(new Date());
    setCountdown(REFRESH_INTERVAL);
  }, [leadSessionMap]);

  // Initial fetch
  useEffect(() => { fetchSessions(false); }, [fetchSessions]);

  // Auto-refresh every 30s + countdown tick
  useEffect(() => {
    const refreshTimer = setInterval(() => {
      fetchSessions(true);
    }, REFRESH_INTERVAL * 1000);

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) return REFRESH_INTERVAL;
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(refreshTimer);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [fetchSessions]);

  const filtered = sessions.filter((s) => {
    if (filter === 'converted') return s.converted;
    if (filter === 'dropped') return !s.converted && (s.opened_form || s.reached_step2);
    if (filter === 'active') return isActive(s.last_seen);
    return true;
  });

  const activeCount = sessions.filter((s) => isActive(s.last_seen)).length;
  const convertedCount = sessions.filter((s) => s.converted).length;
  const droppedCount = sessions.filter((s) => !s.converted && (s.opened_form || s.reached_step2)).length;

  return (
    <>
      {/* Filter tabs */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[
          { key: 'all', label: `Tümü (${sessions.length})`, icon: 'ri-group-line' },
          { key: 'active', label: `Şu An Sitede (${activeCount})`, icon: 'ri-radio-button-line', pulse: true },
          { key: 'dropped', label: `Kayıp (${droppedCount})`, icon: 'ri-door-open-line' },
          { key: 'converted', label: `Lead Oldu (${convertedCount})`, icon: 'ri-checkbox-circle-line' },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setFilter(tab.key as typeof filter)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
            style={{
              background: filter === tab.key ? 'rgba(240,180,41,0.15)' : 'rgba(255,255,255,0.04)',
              color: filter === tab.key ? '#f0b429' : '#6b7280',
              border: `1px solid ${filter === tab.key ? 'rgba(240,180,41,0.3)' : 'rgba(255,255,255,0.06)'}`,
            }}
          >
            {tab.pulse && activeCount > 0 ? (
              <span className="relative flex w-2 h-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full w-2 h-2 bg-green-400" />
              </span>
            ) : (
              <i className={`${tab.icon} text-xs`} />
            )}
            {tab.label}
          </button>
        ))}

        {/* Last updated + countdown refresh button */}
        <div className="ml-auto flex items-center gap-2">
          {lastUpdated && (
            <span className="text-gray-600 text-[10px] hidden sm:block">
              {lastUpdated.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          <button
            type="button"
            onClick={() => fetchSessions(false)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', color: '#6b7280' }}
            title="Şimdi yenile"
          >
            {refreshing ? (
              <i className="ri-loader-4-line text-xs animate-spin" style={{ color: '#f0b429' }} />
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
                <circle
                  cx="12" cy="12" r="10"
                  fill="none"
                  stroke="#f0b429"
                  strokeWidth="3"
                  strokeDasharray={2 * Math.PI * 10}
                  strokeDashoffset={2 * Math.PI * 10 - (countdown / REFRESH_INTERVAL) * 2 * Math.PI * 10}
                  strokeLinecap="round"
                  transform="rotate(-90 12 12)"
                  style={{ transition: 'stroke-dashoffset 1s linear' }}
                />
              </svg>
            )}
            <span style={{ color: refreshing ? '#f0b429' : '#6b7280' }}>
              {refreshing ? 'Yenileniyor...' : `${countdown}sn`}
            </span>
          </button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line text-2xl animate-spin" style={{ color: '#f0b429' }} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <i className="ri-inbox-line text-3xl text-gray-600" />
            <p className="text-gray-500 text-sm">Bu filtrede ziyaretçi yok</p>
          </div>
        ) : (
          filtered.map((s) => {
            const src = getSourceLabel(s.referrer);
            const stage = getJourneyStage(s);
            const active = isActive(s.last_seen);

            return (
              <div
                key={s.session_id}
                className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all"
                style={{
                  background: active ? 'rgba(74,222,128,0.04)' : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${active ? 'rgba(74,222,128,0.15)' : 'rgba(255,255,255,0.05)'}`,
                }}
              >
                {/* Device icon */}
                <div
                  className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: 'rgba(255,255,255,0.05)' }}
                >
                  <i
                    className={s.device === 'mobile' ? 'ri-smartphone-line' : 'ri-computer-line'}
                    style={{ color: s.device === 'mobile' ? '#a78bfa' : '#60a5fa', fontSize: '16px' }}
                  />
                </div>

                {/* Main info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {active && (
                      <span className="flex items-center gap-1 text-[10px] font-bold text-green-400">
                        <span className="relative flex w-1.5 h-1.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                          <span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-green-400" />
                        </span>
                        CANLI
                      </span>
                    )}
                    {s.lead_name ? (
                      <span className="text-white text-xs font-bold">{s.lead_name}</span>
                    ) : (
                      <span className="text-gray-400 text-xs font-mono">
                        {s.session_id.length > 20 ? `${s.session_id.slice(0, 18)}…` : s.session_id}
                      </span>
                    )}
                    <span
                      className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-md"
                      style={{ background: stage.bg, color: stage.color }}
                    >
                      <i className={`${stage.icon} text-[10px]`} />
                      {stage.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <span className="flex items-center gap-1 text-[11px]" style={{ color: src.color }}>
                      <i className={`${src.icon} text-[11px]`} />
                      {src.label}
                    </span>
                    <span className="text-gray-600 text-[11px]">{formatDate(s.first_seen)}</span>
                    <span className="text-gray-600 text-[11px]">
                      <i className="ri-time-line mr-0.5" />{formatDuration(s.time_on_page)}
                    </span>
                    {s.max_scroll > 0 && (
                      <span className="text-gray-600 text-[11px]">
                        <i className="ri-arrow-down-double-line mr-0.5" />%{s.max_scroll} scroll
                      </span>
                    )}
                  </div>
                </div>

                {/* Journey mini-steps */}
                <div className="hidden md:flex items-center gap-1 flex-shrink-0">
                  {[
                    { done: true, icon: 'ri-login-circle-line', tip: 'Siteye Girdi' },
                    { done: s.max_scroll >= 50, icon: 'ri-arrow-down-double-line', tip: '%50 Scroll' },
                    { done: s.clicked_button, icon: 'ri-cursor-line', tip: 'Butona Bastı' },
                    { done: s.opened_form, icon: 'ri-window-2-line', tip: 'Form Açtı' },
                    { done: s.reached_step2, icon: 'ri-arrow-right-circle-line', tip: 'Adım 2' },
                    { done: s.converted, icon: 'ri-whatsapp-line', tip: 'Lead Oldu' },
                  ].map((step, i) => (
                    <div
                      key={i}
                      className="w-6 h-6 flex items-center justify-center rounded-full"
                      style={{
                        background: step.done ? 'rgba(240,180,41,0.15)' : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${step.done ? 'rgba(240,180,41,0.3)' : 'rgba(255,255,255,0.06)'}`,
                      }}
                      title={step.tip}
                    >
                      <i
                        className={`${step.icon} text-[10px]`}
                        style={{ color: step.done ? (step.icon === 'ri-whatsapp-line' ? '#4ade80' : '#f0b429') : '#374151' }}
                      />
                    </div>
                  ))}
                </div>

                {/* Timeline button */}
                <button
                  type="button"
                  onClick={() => setTimelineSession({
                    sessionId: s.session_id,
                    name: s.lead_name ?? `Ziyaretçi (${s.session_id.slice(0, 8)})`,
                  })}
                  className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-500 hover:text-yellow-400 transition-colors cursor-pointer flex-shrink-0"
                  style={{ background: 'rgba(255,255,255,0.05)' }}
                  title="Davranış Geçmişi"
                >
                  <i className="ri-route-line text-sm" />
                </button>
              </div>
            );
          })
        )}
      </div>

      {timelineSession && (
        <BehaviorTimeline
          sessionId={timelineSession.sessionId}
          leadName={timelineSession.name}
          onClose={() => setTimelineSession(null)}
        />
      )}
    </>
  );
}
